import { _ as _objectSpread2 } from './index-c76be973.esm.js';
import { _ as _objectWithoutProperties } from './objectWithoutProperties-7e1dfdab.esm.js';
import { forwardRef } from 'react';
import styled from 'styled-components';
import cuid from 'cuid';
import isChromatic from 'chromatic/isChromatic';
import { S as Spacer } from './index-68cf36c7.esm.js';
import { escapeRegExp, pxToRem, filterGlobalProps } from '@lendi-ui/commons/utils';
import { I as InputBaseComponent, u as useClearAction, F as FieldLabel, C as ClearActionButton, H as HelpText, E as ErrorText } from './TextField-1eb922c0.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';

/**
 * Remove invalid characters
 */
var removeInvalidChars = function removeInvalidChars(value) {
  if (value) {
    var reg = new RegExp("[^\\d.]", 'gi');
    return value.replace(reg, '');
  }
  return '';
};

/**
 * Remove group separator from value eg. 1,000 > 1000
 */
var removeSeparators = function removeSeparators(value) {
  var separator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ',';
  var reg = new RegExp(escapeRegExp(separator), 'g');
  return value.replace(reg, '');
};
var cleanNumericValue = _ref => {
  var {
    value,
    allowDecimal = false
  } = _ref;
  var groupSeparator = ',';
  var decimalSeparator = '.';
  var decimalsLimit = 2;
  var withoutSeparators = removeSeparators(value, groupSeparator);
  var withoutInvalidChars = removeInvalidChars(withoutSeparators);
  var valueOnly = withoutInvalidChars;

  // This is the part that takes care of trimming the decimals
  if (valueOnly.includes(decimalSeparator)) {
    var [int, decimals] = withoutInvalidChars.split(decimalSeparator);
    var trimmedDecimals = decimals ? decimals.slice(0, decimalsLimit) : decimals;
    var includeDecimals = allowDecimal ? "".concat(decimalSeparator).concat(trimmedDecimals) : '';
    return "".concat(int).concat(includeDecimals);
  }
  return valueOnly;
};
var _excluded = ["inputId", "name", "helpText", "errorText", "validationState", "placeholder", "ariaLabel", "ariaLabelledBy", "label", "labelSuffix", "inlineAction", "isRequired", "isDisabled", "value", "defaultValue", "onChange", "onValueChange", "width", "minWidth", "maxWidth", "maxCharCount", "minCharCount"];
var Input = styled(InputBaseComponent).withConfig({
  displayName: "NumericField__Input",
  componentId: "lui__sc-1xofghe-0"
})(["padding-left:", ";padding-right:", ";"], _ref => {
  var {
    variant
  } = _ref;
  return variant && variant === 'dollar' ? pxToRem(46) : pxToRem(16);
}, _ref2 => {
  var {
    hasRightItem
  } = _ref2;
  return hasRightItem ? pxToRem(46) : pxToRem(16);
});
var Wrapper = styled.div.withConfig({
  displayName: "NumericField__Wrapper",
  componentId: "lui__sc-1xofghe-1"
})(["position:relative;width:100%;"]);
function NumericFieldComponent(_ref3, ref) {
  var {
      inputId,
      name,
      helpText,
      errorText,
      validationState,
      placeholder,
      ariaLabel,
      ariaLabelledBy,
      label,
      labelSuffix,
      inlineAction,
      isRequired,
      isDisabled,
      value,
      defaultValue,
      onChange,
      onValueChange,
      width = '100%',
      minWidth,
      maxWidth,
      maxCharCount,
      minCharCount
    } = _ref3,
    props = _objectWithoutProperties(_ref3, _excluded);
  var globalProps = filterGlobalProps(props);
  var inputElId = isChromatic() ? '1' : inputId !== null && inputId !== void 0 ? inputId : cuid();
  var helpTextId = isChromatic() ? '2' : cuid();
  var errorTextId = isChromatic() ? '3' : cuid();
  var hasError = errorText && validationState === 'invalid';
  var handleOnChange = event => {
    var valueOnly = cleanNumericValue({
      value: event.target.value
    });
    event.target.value = valueOnly;
    onChange === null || onChange === void 0 || onChange(event);
    if (valueOnly === '') {
      onValueChange === null || onValueChange === void 0 || onValueChange(undefined, name);
      return;
    }
    onValueChange === null || onValueChange === void 0 || onValueChange(Number(valueOnly), name);
  };
  var {
    isCleared,
    handleChange,
    clearValue
  } = useClearAction(handleOnChange, ref, value === null || value === void 0 ? void 0 : value.toString());
  var ariaDescribedBy = [];
  if (helpText) {
    ariaDescribedBy.push(helpTextId);
  }
  if (hasError) {
    ariaDescribedBy.push(errorTextId);
  }
  return /*#__PURE__*/jsxs(Spacer, _objectSpread2(_objectSpread2({
    ygap: "xxs",
    width: width,
    maxWidth: maxWidth,
    minWidth: minWidth
  }, globalProps), {}, {
    children: [label && /*#__PURE__*/jsx(FieldLabel, {
      forId: inputElId,
      label: label,
      labelSuffix: labelSuffix
    }), /*#__PURE__*/jsxs(Spacer, {
      ygap: "xxs",
      children: [/*#__PURE__*/jsxs(Wrapper, {
        children: [/*#__PURE__*/jsx(Input, {
          id: inputElId,
          name: name,
          type: "text",
          pattern: "[0-9]*",
          placeholder: placeholder,
          validationState: validationState,
          "aria-invalid": validationState === 'invalid',
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          "aria-describedby": ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined,
          required: isRequired,
          disabled: isDisabled,
          maxLength: maxCharCount,
          minLength: minCharCount,
          value: value && isCleared ? '' : value,
          defaultValue: defaultValue,
          onChange: handleChange,
          ref: ref,
          hasRightItem: !!inlineAction,
          inputMode: "numeric"
        }), inlineAction === 'clear' && /*#__PURE__*/jsx(ClearActionButton, {
          isCleared: isCleared,
          clearValue: clearValue
        })]
      }), helpText && /*#__PURE__*/jsx(HelpText, {
        id: helpTextId,
        text: helpText
      }), hasError && errorText && /*#__PURE__*/jsx(ErrorText, {
        id: errorTextId,
        text: errorText
      })]
    })]
  }));
}
var NumericField = /*#__PURE__*/forwardRef(NumericFieldComponent);
export { NumericField as N, cleanNumericValue as c };